export const deviceSchemaKeys = {
	all: ['deviceSchema'] as const,
	/** Prefix over every device_state schema entry, for invalidation after a column create, rename or delete. */
	allStateKeys: () => [...deviceSchemaKeys.all, 'stateKeys'] as const,
	/** One project's state-key definitions (derived states folded in). Impersonation is part of the
	 *  key, as it is for metadata below: it changes which keys the endpoint returns. */
	stateKeys: (tenantId: string, impersonateAdmin: boolean) =>
		[...deviceSchemaKeys.allStateKeys(), tenantId, impersonateAdmin] as const,
	/** Prefix over every metadata-key entry, for invalidation after a create/rename/delete. */
	allMetadataKeys: () => [...deviceSchemaKeys.all, 'metadataKeys'] as const,
	/** One project's metadata-key definitions. Impersonation is part of the key: it changes the
	 *  keys the endpoint returns, so the two views must not share an entry. */
	metadataKeys: (tenantId: string, impersonateAdmin: boolean) =>
		[...deviceSchemaKeys.allMetadataKeys(), tenantId, impersonateAdmin] as const,
	/** Prefix over every stream schema a device filter bar resolves column types from, for invalidation. */
	allStateKeyStreams: () => [...deviceSchemaKeys.all, 'stateKeyStreams'] as const,
	/** Schemas of the streams one project's state keys point at. */
	stateKeyStreams: (tenantId: string, streamNames: readonly string[]) =>
		[...deviceSchemaKeys.allStateKeyStreams(), tenantId, streamNames.join(',')] as const,
}

export const actionKeys = {
	all: ['actions'] as const,
	/** Exact-id lookup of one device inside an action's device list (progress-log device dropdown search).
	 *  `tenantId` is '' at project scope. */
	deviceSearch: (actionId: number, tenantId: string, term: string) =>
		[...actionKeys.all, 'deviceSearch', actionId, tenantId, term] as const,
	/** Every platform event for one (action, device), unpaginated. `tenantId` is '' at project scope. */
	/** The (action, device) the platform events belong to, the prefix every time window of it shares. */
	platformLogsScope: (actionId: number, deviceId: string, tenantId: string) =>
		[...actionKeys.all, 'platformLogs', actionId, deviceId, tenantId] as const,
	/** Platform events inside one time window; the window is applied server side. */
	platformLogs: (actionId: number, deviceId: string, tenantId: string, timeRange: unknown) =>
		[...actionKeys.platformLogsScope(actionId, deviceId, tenantId), timeRange] as const,
	/** The (action, device) a status-logs page belongs to — the prefix every page of it shares. */
	statusLogsScope: (actionId: number, deviceId: string, tenantId: string) =>
		[...actionKeys.all, 'statusLogs', actionId, deviceId, tenantId] as const,
	/** One page of `action_status` rows. `view` carries the sort, row filters and time range, which
	 *  all change what the page returns. */
	statusLogs: (
		actionId: number,
		deviceId: string,
		tenantId: string,
		page: number,
		perPage: number,
		view: Record<string, unknown>,
	) => [...actionKeys.statusLogsScope(actionId, deviceId, tenantId), page, perPage, view] as const,
	/** One action's payload and settings. `tenantId` is '' at project scope. `includeFileInfo` is part
	 *  of the key because it changes the response shape — it merges checksum/size/url into `payload`. */
	payload: (actionId: number, tenantId: string, includeFileInfo: boolean) =>
		[...actionKeys.all, 'payload', actionId, tenantId, includeFileInfo] as const,
	/** Latency histogram of every state of an action. `tenantId` is '' at project scope. */
	latency: (actionId: number, tenantId: string) => [...actionKeys.all, 'latency', actionId, tenantId] as const,
	/** Every failed device of an action, for the failure distribution. `tenantId` is '' at project scope. */
	failedDevices: (actionId: number, tenantId: string) =>
		[...actionKeys.all, 'failedDevices', actionId, tenantId] as const,
	/** An action's devices, paged as the progress-log dropdown scrolls. `tenantId` is '' at project scope. */
	devices: (actionId: number, tenantId: string) => [...actionKeys.all, 'devices', actionId, tenantId] as const,
	/** Fleet-wide attaches still queued or in progress — the exclusion source for the attach form's pickers. */
	inflightAttaches: (organizationId: string) => [...actionKeys.all, 'inflightAttaches', organizationId] as const,
}

export const deviceKeys = {
	all: ['devices'] as const,
	/** Prefix matching every filter-options suggestion query, across all scopes — for invalidation. */
	allFilterOptions: () => [...deviceKeys.all, 'filterOptions'] as const,
	/** Base key for filter-options suggestions. `scope` = dataset context (project/org, status, filters). */
	filterOptions: (scope: string) => [...deviceKeys.allFilterOptions(), scope] as const,
	/** The full filter-options response (every column's distinct count). `scope` = project/org + id. */
	filterOptionCounts: (scope: string) => [...deviceKeys.allFilterOptions(), 'counts', scope] as const,
	/** Infinite-query key for one column's paginated/searched value suggestions. */
	columnFilterOptions: (scope: string, columnId: string, search: string) =>
		[...deviceKeys.filterOptions(scope), 'column', columnId, search] as const,
	/** One device the bottom bar shows but the loaded table rows don't hold. `scope` = project/org + status. */
	barDevice: (scope: string, deviceId: string) => [...deviceKeys.all, 'barDevice', scope, deviceId] as const,
	/** `tenantId` is the project the rows come from: device ids repeat across projects. */
	streamLastSeen: (tenantId: string, deviceId: string, streamsKey: string) =>
		[...deviceKeys.all, 'streamLastSeen', tenantId, deviceId, streamsKey] as const,
	/** Paginated device-state search (Streams page → device_state → View Data). */
	stateSearch: (params: Record<string, unknown>) => [...deviceKeys.all, 'stateSearch', params] as const,
	/** Infinite-query base key for the lazy DeviceDropdown list. `scope` = project/org + status + tenant;
	 *  DeviceDropdown appends the serialized filters. */
	dropdown: (scope: string) => [...deviceKeys.all, 'dropdown', scope] as const,
	/** One derived-state membership query: which of `idsKey`'s devices match `statusKey`. */
	statusMembership: (scope: string, statusKey: string, idsKey: string) =>
		[...deviceKeys.all, 'statusMembership', scope, statusKey, idsKey] as const,
	/** Prefix matching every archived-membership query — for invalidation after archive/unarchive. */
	allArchived: () => [...deviceKeys.all, 'archived'] as const,
	/** Which of `idsKey`'s devices are archived. `scope` = tenant, since device ids repeat per project. */
	archived: (scope: string, idsKey: string) => [...deviceKeys.allArchived(), scope, idsKey] as const,
	/** One page of device ids for a device-wise fleet panel (LED/Last Value/Aggregate Value).
	 *  `scope` = tenant + fleet filters + activity window, so panels on one dashboard share a page. */
	fleetPanelDeviceIds: (scope: string, page: number, perPage: number) =>
		[...deviceKeys.all, 'fleetPanelDeviceIds', scope, page, perPage] as const,
	/** Active devices for metadata lookups and device pickers. `scope` = tenant, so every consumer
	 *  on a page (dashboard header, panels, device dropdown) shares one fetch. */
	activeDevices: (scope: string) => [...deviceKeys.all, 'activeDevices', scope] as const,
	/** One page of a Device Pulse bucket's devices. `scope` = tenant + fleet filters + bucket bounds +
	 *  when those bounds were resolved, so paging stays on one frozen window. */
	pulseBucketDevices: (scope: string, page: number, perPage: number) =>
		[...deviceKeys.all, 'pulseBucketDevices', scope, page, perPage] as const,
	/** Device metadata for a panel's configured keys, unpaginated. `scope` = tenant + the key list,
	 *  so panels asking for the same keys share one fetch. */
	metadataForKeys: (scope: string) => [...deviceKeys.all, 'metadataForKeys', scope] as const,
	/** How many devices one tab holds, from a `per_page: 1` search. `scope` = tenant + status. */
	count: (scope: string) => [...deviceKeys.all, 'count', scope] as const,
	/** One page of the dashboard device picker's own list. `scope` = tenant + status + the built query,
	 *  which already folds in the dashboard's preset scope. */
	picker: (scope: string, page: number, perPage: number) =>
		[...deviceKeys.all, 'picker', scope, page, perPage] as const,
}
